import { useEffect, useMemo, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { toast } from "sonner";
import { recordClick } from "../lib/api";
import { saveQuoteProgress } from "../lib/personalize";
import { useGeo } from "../lib/geo";
import { track, events } from "../lib/analytics";
import { useExperiment } from "../lib/experiments";
import { routeIntake } from "../lib/partnerRouting";
import QuoteSuccessCard from "./QuoteSuccessCard";
import FunnelProgress from "./funnel/FunnelProgress";
import MatchingState from "./funnel/MatchingState";
import {
  ShieldCheck,
  Banknote,
  Wrench,
  ArrowRight,
  MapPin,
  Car,
} from "lucide-react";
import {
  CTA_INSURANCE,
  CTA_AUTO_LOAN,
  CTA_REFINANCE,
  CTA_WARRANTY,
} from "../lib/ctaVocabulary";

// Each tab is one funnel category. The "auto-loan" tab is special: it
// hosts a New/Used sub-toggle whose value becomes the submitted category
// (auto-loan-new or auto-loan-used).
//
// Iter-174 — Per-tab CTAs match user intent (the iter-166 universal
// "Compare Options" was too generic across categories). NerdWallet /
// Bankrate / Policygenius style: the CTA names the action the user
// will take on the next surface.
//
// Iter-182 — Refined again for benefit-driven intent vocabulary.
// Insurance "Compare Insurance Quotes" (was "Compare Rates" — more
// specific to the action), Auto Loan "Find Loan Offers" (was "Compare
// Loan Offers" — frames discovery vs. comparison), Refinance "Lower My
// Payment" (was "See Refinance Offers" — leads with the user benefit),
// Warranty "Compare Protection Plans" (was "Compare Warranty Plans" —
// reframes warranty as protection).
// Iter-185.BJ — Per-tab CTAs reference the central `ctaVocabulary`
// constants so any future vocabulary tweak ripples to every surface
// from one place.
const TABS = [
  {
    id: "insurance",
    label: "Insurance",
    icon: ShieldCheck,
    cta: CTA_INSURANCE,
  },
  {
    id: "auto-loan",
    label: "Auto Loan",
    icon: Car,
    cta: CTA_AUTO_LOAN,
    hasSubToggle: true,
  },
  {
    id: "refinance",
    label: "Refinance",
    icon: Banknote,
    cta: CTA_REFINANCE,
  },
  {
    id: "warranty",
    label: "Warranty",
    icon: Wrench,
    cta: CTA_WARRANTY,
  },
];

const TAB_IDS = TABS.map((t) => t.id);

// Map external "category" prop values (used by QuoteModal + URL params) into
// the internal tab + sub-toggle state.
function categoryToState(category) {
  if (category === "auto-loan-new") return { tab: "auto-loan", sub: "new" };
  if (category === "auto-loan-used") return { tab: "auto-loan", sub: "used" };
  if (TAB_IDS.includes(category)) return { tab: category, sub: "new" };
  return { tab: "insurance", sub: "new" };
}

// Internal state → backend category value.
function stateToCategory(tab, sub) {
  if (tab === "auto-loan") return sub === "used" ? "auto-loan-used" : "auto-loan-new";
  return tab;
}

export default function QuoteForm({
  id = "hero-form",
  compact = false,
  defaultCategory,
  successContext,
}) {
  const initial = useMemo(() => categoryToState(defaultCategory), [defaultCategory]);
  const [tab, setTab] = useState(initial.tab);
  const [autoSub, setAutoSub] = useState(initial.sub); // "new" | "used"
  const [zip, setZip] = useState("");
  const [loading, setLoading] = useState(false);
  const [geoHint, setGeoHint] = useState(null);
  const [autoFilled, setAutoFilled] = useState(false);
  // Iter-196.WW (L-6) — Honeypot anti-spam field. Real users never
  // see the off-screen input; bot frameworks fill every field they
  // find and get routed to quote_requests_spam by the backend.
  const [honeypot, setHoneypot] = useState("");
  // After a successful submit, replace the form with a personalized success
  // card. When `successContext` was passed from the estimator handoff, the
  // card renders a rate-range projection ("drivers in California with your
  // profile typically see $1,435–$2,358/yr"). When no context exists, falls
  // back to a generic success card. Stored as the submission payload so we
  // can also surface the submitted ZIP in the success copy.
  const [submission, setSubmission] = useState(null);
  // Iter-166 — Brief "matching providers…" transition state between
  // submit-success + the success card render. Dwells for ~1.5 sec to
  // make the funnel feel intentional. The actual API call is awaited
  // before this state mounts, so the dwell never blocks a real error.
  const [matching, setMatching] = useState(false);

  // If `defaultCategory` changes (e.g. modal re-opened with a different
  // intent), reconcile state to the new value.
  useEffect(() => {
    if (!defaultCategory) return;
    const next = categoryToState(defaultCategory);
    setTab(next.tab);
    setAutoSub(next.sub);
  }, [defaultCategory]);

  const { geo } = useGeo();

  // Iter-82 — Hero primary CTA copy A/B experiment. Backend-registered
  // experiment `hero_primary_cta`. Variant `savings` swaps the CTA label
  // across all tabs; `control` keeps each tab's editorial copy.
  const heroCta = useExperiment("hero_primary_cta", { label: null });
  const ctaLabelFor = (defaultLabel) =>
    id === "hero-form" && heroCta.payload?.label
      ? heroCta.payload.label
      : defaultLabel;

  useEffect(() => {
    track(events.ZIP_FORM_VIEW, { location: id });
  }, [id]);

  // Apply auto-detected ZIP once geo resolves (only if user hasn't typed).
  useEffect(() => {
    if (!geo) return;
    if (geo.zip && /^\d{5}$/.test(String(geo.zip))) {
      setZip((prev) => {
        if (prev && prev.length > 0) return prev;
        setAutoFilled(true);
        return String(geo.zip);
      });
    }
    if (geo.city && geo.region) {
      setGeoHint({ city: geo.city, region: geo.region });
    }
  }, [geo]);

  const onZipChange = (e) => {
    setZip(e.target.value.replace(/\D/g, ""));
    setAutoFilled(false);
  };

  const category = stateToCategory(tab, autoSub);

  // Iter-176 — Per-tab CTA label resolved at submit time. Used by the
  // HERO_TAB_CTA_CLICK event so the analytics team can see which CTA
  // text (Compare Rates / Compare Loan Offers / See Refinance Offers /
  // Compare Warranty Plans) converts best per-tab.
  const ctaLabelForCurrentTab = () => {
    const t = TABS.find((x) => x.id === tab);
    return t ? ctaLabelFor(t.cta) : null;
  };

  const submit = async (e) => {
    e.preventDefault();
    // Iter-176 — Fire the CTA click event BEFORE validation. We want
    // the analytics team to see every CTA tap (including invalid-ZIP
    // mistypes) so the click-through funnel is comprehensive.
    track(events.HERO_TAB_CTA_CLICK, {
      category,
      cta_label: ctaLabelForCurrentTab(),
      source: id,
    });
    if (!/^\d{5}$/.test(zip)) {
      track(events.ZIP_FORM_ERROR, { reason: "invalid_zip", category });
      return toast.error("Enter a valid 5-digit ZIP");
    }
    setLoading(true);
    track(events.ZIP_FORM_SUBMIT, { category, zip_prefix: zip.slice(0, 3) });
    recordClick({
      target: `quote_form:${category}`,
      source_page: typeof window !== "undefined" ? window.location.pathname : "/",
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
    });
    try {
      // Iter-89 — Use `routeIntake` so the homepage hero form honors the
      // admin-managed PartnerRoutingPanel registry (same one that powers the
      // multi-step intake modal). Categories flipped to `affiliate-link` in
      // /admin/routing now redirect users from the hero too; categories still
      // on `lead-capture` (the default) render the same in-card success UX
      // as before.
      const result = await routeIntake({
        category,
        intake: { zip_code: zip },
        honeypot,
      });

      if (result.status === "error") {
        track(events.ZIP_FORM_ERROR, { reason: "server_error", category });
        toast.error("Something went wrong. Try again.");
        return;
      }

      track(events.ZIP_FORM_SUCCESS, {
        category,
        zip_prefix: zip.slice(0, 3),
        quote_id: result?.quote_id,
        routing_status: result.status,
        personalized_success: Boolean(successContext),
      });

      // Iter-64 — save progress for the "pick up where you left off" banner.
      saveQuoteProgress(category, zip);

      if (result.status === "redirected") {
        // Partner tab opened in a new window — let the user know + leave
        // the form mounted so they can run another category if they want.
        toast.success(
          "Opened your partner match in a new tab — finish there to lock in your rate.",
        );
        return;
      }

      // Default `lead-capture` strategy — render the personalized success card.
      // Iter-167 — Show the matching transition state for ~2 sec, then
      // flip to the success card. Spec dwell: 2 seconds.
      setMatching(true);
      setTimeout(() => {
        setMatching(false);
        setSubmission({ zip, category, quote_id: result?.quote_id });
        toast.success("Got it — we'll start comparing rates in your area.");
      }, 2000);
    } catch (err) {
      // Iter-90 — Log the actual error message so server_error reasons are
      // debuggable in PostHog / GA4. Previously this catch silently swallowed
      // the message which made production triage painful.
      track(events.ZIP_FORM_ERROR, {
        reason: "server_error",
        category,
        error_message: err?.message || String(err),
      });
      // eslint-disable-next-line no-console
      console.error("[carsavr] ZIP form submit failed:", err);
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // Iter-166 — Render the matching transition state between submit
  // success and the personalized success card. Lives between the
  // submission flag being set above + the success card flipping in.
  if (matching) {
    return <MatchingState compact={compact} />;
  }

  // When a successful submission lands, short-circuit to the success card.
  if (submission) {
    return (
      <QuoteSuccessCard
        submission={submission}
        successContext={successContext}
        compact={compact}
        onReset={() => setSubmission(null)}
      />
    );
  }

  return (
    <div
      id={id}
      data-testid="quote-form-card"
      data-suppress-floating-proof
      className={`relative bg-white border border-slate-200 ${compact ? "p-0 border-0" : "pt-7 px-4 pb-4 sm:p-6 md:p-8"}`}
    >
      {!compact && (
        <div
          data-testid="quote-form-free-pill"
          data-iter="185.S"
          className="absolute -top-2.5 left-4 sm:left-6 bg-[#10B981] text-slate-950 text-[11px] sm:text-[12px] font-display font-black tracking-[-0.01em] px-2.5 py-1 sm:px-3 sm:py-1"
        >
          Free · 2 min to compare
        </div>
      )}

      {/* Iter-166 — Unified 3-step funnel progress. Mirrors the modal
          experience so the journey markers (Enter Details → Matching
          Providers → Compare Options) appear on every entry surface.
          Iter-182 — Tightened margins on mobile (mb-4 vs. mb-5) so the
          form fits more above the fold. */}
      <div className={compact ? "mb-3.5" : "mb-4"}>
        <FunnelProgress step={1} state="active" compact={compact} />
      </div>

      {/* Iter-174 — Short value-prop line above the form. Tells the user
          why they should interact + frames the funnel as a fast / free
          comparison action. Hidden in compact (modal) mode since the
          modal already carries its own subhead.
          Iter-182 — Copy retuned for benefit-driven framing
          (the previous iter-174 first-draft microcopy referenced
          overpaying; retired in favor of the savings-before-payment
          hook below). */}
      {!compact && (
        <p
          data-testid="quote-form-value-prop"
          className="text-[12px] sm:text-[13px] text-slate-600 leading-snug mb-3 sm:mb-4"
        >
          Discover how much you could save before your next payment.
        </p>
      )}

      {/* Iter-176 — Tab selection event tracks which category users hover
          toward. The `Tabs` onValueChange callback is wrapped to emit the
          new event alongside React state updates. */}
      <Tabs
        value={tab}
        onValueChange={(next) => {
          if (next !== tab) {
            track(events.HERO_TAB_SELECT, { category: next, source: id });
          }
          setTab(next);
        }}
        className="w-full"
      >
        <TabsList className="grid grid-cols-4 w-full bg-slate-100 rounded-none p-1 h-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger
                key={t.id}
                value={t.id}
                data-testid={`quote-tab-${t.id}`}
                className="rounded-none data-[state=active]:bg-slate-950 data-[state=active]:text-white font-semibold min-h-[44px] py-2.5 text-[11px] sm:text-xs md:text-sm flex items-center gap-1.5 justify-center"
              >
                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>{t.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {TABS.map((t) => (
          <TabsContent key={t.id} value={t.id} className="mt-4 sm:mt-6">
            {t.hasSubToggle && (
              <div
                role="radiogroup"
                aria-label="New or used car"
                className="mb-4 grid grid-cols-2 border border-slate-300 bg-white text-sm font-bold"
              >
                {[
                  { id: "new", label: "New Car" },
                  { id: "used", label: "Used Car" },
                ].map((opt) => {
                  const active = autoSub === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      data-testid={`quote-subtoggle-${opt.id}`}
                      onClick={() => setAutoSub(opt.id)}
                      className={`min-h-[44px] py-2.5 px-3 transition-colors text-sm font-bold ${
                        active
                          ? "bg-slate-950 text-white"
                          : "bg-white text-slate-600 hover:text-slate-950"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
            <form
              onSubmit={submit}
              aria-label="Compare rates by ZIP code"
              className="space-y-3 sm:space-y-4"
            >
              {/* Iter-196.WW (L-6) — Honeypot anti-spam field. Hidden
                  off-screen from real users (aria-hidden + tabIndex=-1
                  + screen-reader-hidden CSS) but spider bots fill it
                  anyway. Backend dead-letters any submission with a
                  populated `website` field into quote_requests_spam. */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                data-testid="quote-form-honeypot"
                className="absolute left-[-9999px] top-[-9999px] h-0 w-0 opacity-0 pointer-events-none"
              />
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 block mb-2 flex items-center justify-between gap-2">
                  <span>ZIP Code</span>
                  {autoFilled && geoHint && (
                    <span
                      data-testid="zip-autofill-hint"
                      className="inline-flex items-center gap-1 text-[10px] font-bold text-[#10B981] normal-case tracking-normal"
                    >
                      <MapPin className="w-3 h-3" />
                      <span className="normal-case tracking-normal">
                        {geoHint.city}, {geoHint.region} detected
                      </span>
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  value={zip}
                  onChange={onZipChange}
                  placeholder="90210"
                  data-testid="zip-input"
                  aria-required="true"
                  aria-invalid={zip.length > 0 && !/^\d{5}$/.test(zip)}
                  autoComplete="postal-code"
                  enterKeyHint="next"
                  className="w-full h-12 sm:h-14 px-4 bg-white border border-slate-300 focus:border-slate-950 outline-none text-2xl font-display font-bold tracking-tight transition-colors"
                />
                {/* Iter-91 — Picked JS validation as the single source of
                    truth (was previously HTML5 `pattern` + `required` that
                    blocked the JS toast). Sonner toast on submit gives the
                    same UX as every other CarSavr form. `aria-required` +
                    `aria-invalid` preserve a11y semantics without forcing
                    the browser's native popup. */}
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="zip-submit-button"
                className="lift press w-full h-12 sm:h-14 bg-slate-950 hover:bg-slate-800 text-white font-bold flex items-center justify-center gap-2 group transition-all duration-200"
              >
                {loading ? "Searching…" : ctaLabelFor(t.cta)}
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                No spam. We never sell your info. By submitting, you agree to our{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener"
                  data-testid="quote-form-terms-link"
                  className="text-slate-950 underline hover:no-underline transition-all"
                >
                  Terms
                </a>
                .
              </p>
            </form>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
